import { FRB_PRIVATE_PKCS8 } from '@/pem/frb_private'
import { IS_PRIVATE } from '@/common/const/env'
import { FRB_PRIVATE_INTRANET_PKCS8 } from '@/pem/frb_private_intranet'

export interface DecryptWebSocketUrlParams {
  encrypt_key: string
  nonce: string
  ciphertext: string
}

/**
 * 前端解密函数 - 与后端混合加密系统对接
 * @param {string} privateKeyPEM - PEM格式的RSA私钥
 * @param {ArrayBuffer} encryptedKey - 加密的AES密钥（ArrayBuffer格式）
 * @param {ArrayBuffer} nonce - AES-GCM使用的Nonce值（ArrayBuffer格式）
 * @param {ArrayBuffer} ciphertext - AES-GCM加密的密文（ArrayBuffer格式）
 * @returns {Promise<string>} 解密后的原始消息
 */
async function hybridDecrypt (privateKeyPEM, encryptedKey, nonce, ciphertext) {
  try {
    // 1. 解析PEM格式的RSA私钥
    const privateKey = await parseRsaPrivateKeyPEM(privateKeyPEM)

    // 2. 使用RSA私钥解密获取AES密钥
    const aesKey = await decryptAesKeyWithRSA(privateKey, encryptedKey)

    // 3. 使用AES密钥和Nonce解密数据
    const plaintext = await decryptDataWithAesGcm(aesKey, nonce, ciphertext)

    return plaintext
  } catch (error) {
    console.error('解密过程出错:', error)
    throw error
  }
}

/**
 * 解析PEM格式的RSA私钥
 * @param {string} pemString - PEM格式的私钥字符串
 * @returns {CryptoKey} RSA私钥对象
 */
async function parseRsaPrivateKeyPEM (pemString) {
  //  base64解码
  const binaryDer = window.atob(pemString)
  const der = new Uint8Array(binaryDer.length)

  for (let i = 0; i < binaryDer.length; i++) {
    der[i] = binaryDer.charCodeAt(i)
  }

  // 导入RSA私钥
  return window.crypto.subtle.importKey(
    'pkcs8',
    der,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256'
    },
    false,
    ['decrypt']
  )
}

/**
 * 使用RSA私钥解密AES密钥
 * @param {CryptoKey} rsaPrivateKey - RSA私钥对象
 * @param {ArrayBuffer} encryptedAesKey - 加密的AES密钥
 * @returns {ArrayBuffer} 解密后的AES密钥
 */
async function decryptAesKeyWithRSA (rsaPrivateKey, encryptedAesKey) {
  return window.crypto.subtle.decrypt(
    {
      name: 'RSA-OAEP'
    },
    rsaPrivateKey,
    encryptedAesKey
  )
}

/**
 * 使用AES-GCM解密数据
 * @param {ArrayBuffer} aesKey - AES密钥
 * @param {ArrayBuffer} nonce - 随机数
 * @param {ArrayBuffer} ciphertext - 密文
 * @returns {string} 解密后的明文
 */
async function decryptDataWithAesGcm (aesKey, nonce, ciphertext) {
  // 导入AES密钥
  const aesKeyObj = await window.crypto.subtle.importKey(
    'raw',
    aesKey,
    {
      name: 'AES-GCM',
      length: 256 // 关键参数，指定256位（32字节）
    },
    false,
    ['decrypt']
  )

  // 解密数据
  const decrypted = await window.crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: nonce,
      tagLength: 128
    },
    aesKeyObj,
    ciphertext
  )

  // 将ArrayBuffer转换为字符串
  return new TextDecoder().decode(decrypted)
}

/**
 * 辅助函数：将Base64字符串转换为ArrayBuffer
 * @param {string} base64String - Base64字符串
 * @returns {ArrayBuffer} ArrayBuffer
 */
function base64ToArrayBuffer (base64String) {
  const binaryString = window.atob(base64String)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return bytes.buffer
}

export async function decryptWebSocketUrl (params: DecryptWebSocketUrlParams) {
  try {
    // 将Base64数据转换为ArrayBuffer
    const encryptedKey = base64ToArrayBuffer(params.encrypt_key)
    const nonce = base64ToArrayBuffer(params.nonce)
    const ciphertext = base64ToArrayBuffer(params.ciphertext)

    // 调用解密函数
    const plaintext = await hybridDecrypt(
      IS_PRIVATE ? FRB_PRIVATE_INTRANET_PKCS8 : FRB_PRIVATE_PKCS8,
      encryptedKey,
      nonce,
      ciphertext
    )

    return plaintext
  } catch (error) {
    console.error('解密失败:', error)
  }
}
